import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import classname from 'classnames';

import {
	compose,
	withContext,
	withState,
	defaultProps,
	withHandlers,
	pure
} from 'recompose';

import { GOOGLE, MAP } from './constants';

export const _initializeMap = ({
	google, setMap, center, zoom, mapControls, [MAP]: map, handleIdle, initialBounds, handleZoom, onZoomChanged,
	setPrevZoom, onIdle, setDidDrag, gestureHandling = 'greedy', preselectedFacilityId
}, node) => {
	if (node && google && !map) {
		// Set the map style for 2 reasons:
		// 1. We don't want markers for the default POIs on the map
		// 2. Setting the style this way changes to Google logo on the map to all white, which looks better.
		const styledMapType = new google.maps.StyledMapType([
			{
				featureType: 'landscape',
				elementType: 'labels',
				stylers: [
					{ visibility: 'off' }
				]
			},
			{
				featureType: 'poi',
				elementType: 'labels',
				stylers: [
					{ visibility: 'off' }
				]
			}
		]);

		const newMap = new google.maps.Map(node, {
			center,
			zoom,
			clickableIcons: false,
			gestureHandling,
			mapTypeControlOptions: {
				mapTypeIds: ['roadmap', 'styled_map']
			},
			...mapControls
		});

		const l = newMap.addListener('idle', () => {
			if (initialBounds && !preselectedFacilityId) {
				newMap.fitBounds(initialBounds);
				const newZoom = newMap.getZoom();
				setPrevZoom(newZoom);
				onZoomChanged(newZoom);
			}

			google.maps.event.removeListener(l);
			onIdle(newMap);
			newMap.addListener('idle', () => handleIdle(newMap));
			newMap.addListener('zoom_changed', () => handleZoom(newMap.getZoom()));
			newMap.addListener('dragend', () => setDidDrag(true));
		});

		newMap.mapTypes.set('styled_map', styledMapType);
		newMap.setMapTypeId('styled_map');
		setMap(newMap);
	}
};

export const enhance = compose(
	defaultProps({
		onZoomChanged: noop,
		onIdle: noop,
		onDragEnd: noop,
		onBoundsChange: noop,
		onRef: noop,
		dragThreshold: 1000,
		mapControls: {
			zoomControl: false,
			mapTypeControl: false,
			scaleControl: false,
			streetViewControl: false,
			rotateControl: false,
			fullscreenControl: false
		}
	}),
	pure,
	withState(MAP, 'setMap', null),
	withState('bounds', 'setBounds', null),
	withState('idleEventDisabled', 'setDisableIdleEvent', null),
	withState('prevZoom', 'setPrevZoom', null),
	withState('didDrag', 'setDidDrag', false),
	withContext(
		{ [MAP]: PropTypes.any, [GOOGLE]: PropTypes.any, disableIdleEvent: PropTypes.func },
		({ [MAP]: map, google, setDisableIdleEvent }) => ({
			[MAP]: map,
			[GOOGLE]: google,
			disableIdleEvent: setDisableIdleEvent
		})
	),
	withHandlers({
		handleZoom: (props) => (newZoom) => {
			const {
				[MAP]: map, onZoomChanged, onBoundsChange, setPrevZoom
			} = props;

			onZoomChanged(newZoom);
			if (map) {
				onBoundsChange(map.getBounds());
			}

			setPrevZoom(newZoom);
		},
		handleDrag: ({ setDidDrag }) => () => {
			setDidDrag(true);
		},
		handleIdle: ({
			google, onBoundsChange, center, dragThreshold, onIdle, bounds,
			setBounds, idleEventDisabled, setDidDrag, didDrag
		}) => (map) => {
			if (!idleEventDisabled) {
				onIdle();
				setDidDrag(false);
				const newBounds = map.getBounds();

				if (newBounds && didDrag) {
					// Determine how far the map was dragged
					const p1 = (bounds && bounds.getCenter()) || (new google.maps.LatLng(center));
					const p2 = newBounds.getCenter();
					const distance = google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
					if (distance > dragThreshold) {
						onBoundsChange(newBounds);
						setBounds(newBounds);
					}
				}
			}
		}
	})
);

export class GoogleMap extends PureComponent {
	componentDidMount() {
		_initializeMap(this.props, this._ref);
	}

	componentDidUpdate({ zoom: prevZoom }) {
		const map = this.props[MAP];
		if ((prevZoom !== this.props.zoom) && map) {
			map.setZoom(this.props.zoom);
		}
	}

	render() {
		const { className, children } = this.props;
		return (
			<div className={classname('ph-google-map', className)}>
				<div className="gmap" ref={(ref) => { this._ref = ref; }} />
				{children}
			</div>
		);
	}
}

GoogleMap.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node,
	zoom: PropTypes.number
};

export default enhance(GoogleMap);
